/**
 * useIndustryVehicles Hook
 * CRUD operations for industry_vehicles table (Auto Repair)
 * Automatically filters by company_id
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CompanyType } from '@/types/company';
import type { IndustryVehicle } from '@/types/industry';
import { useToast } from '@/components/ui/use-toast';

interface UseIndustryVehiclesOptions {
  domain?: CompanyType;
  client_id?: number;
}

interface CreateVehicleInput {
  client_id: number;
  license_plate: string;
  make?: string;
  model?: string;
  year?: number;
  vin?: string;
  color?: string;
  mileage?: number;
  metadata?: Record<string, any>;
}

interface UpdateVehicleInput extends Partial<CreateVehicleInput> {
  id: string;
}

export function useIndustryVehicles(options: UseIndustryVehiclesOptions = {}) {
  const { client_id } = options;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Get current company
  const { data: company } = useQuery({
    queryKey: ['current_company'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Fetch vehicles
  const {
    data: vehicles,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['industry_vehicles', company?.id, client_id],
    queryFn: async () => {
      if (!company) return [];

      let query = supabase
        .from('industry_vehicles')
        .select('*')
        .eq('company_id', company.id)
        .order('created_at', { ascending: false });

      if (client_id) {
        query = query.eq('client_id', client_id);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as IndustryVehicle[];
    },
    enabled: !!company,
  });

  // Create vehicle
  const createVehicle = useMutation({
    mutationFn: async (input: CreateVehicleInput) => {
      if (!company) throw new Error('No company found');

      const { data, error } = await supabase
        .from('industry_vehicles')
        .insert({
          ...input,
          company_id: company.id,
          metadata: input.metadata || {},
        })
        .select()
        .single();

      if (error) throw error;
      return data as IndustryVehicle;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['industry_vehicles', company?.id],
      });
      toast({
        title: 'Veículo criado',
        description: `Veículo ${data.license_plate} foi criado com sucesso.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao criar veículo',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update vehicle
  const updateVehicle = useMutation({
    mutationFn: async (input: UpdateVehicleInput) => {
      const { id, ...updates } = input;

      const { data, error } = await supabase
        .from('industry_vehicles')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as IndustryVehicle;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['industry_vehicles', company?.id],
      });
      toast({
        title: 'Veículo atualizado',
        description: `Veículo ${data.license_plate} foi atualizado com sucesso.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao atualizar veículo',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete vehicle
  const deleteVehicle = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('industry_vehicles')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['industry_vehicles', company?.id],
      });
      toast({
        title: 'Veículo excluído',
        description: 'O veículo foi excluído com sucesso.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao excluir veículo',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    vehicles: vehicles || [],
    isLoading,
    error,
    refetch,
    createVehicle: createVehicle.mutate,
    createVehicleAsync: createVehicle.mutateAsync,
    updateVehicle: updateVehicle.mutate,
    updateVehicleAsync: updateVehicle.mutateAsync,
    deleteVehicle: deleteVehicle.mutate,
    deleteVehicleAsync: deleteVehicle.mutateAsync,
    isCreating: createVehicle.isPending,
    isUpdating: updateVehicle.isPending,
    isDeleting: deleteVehicle.isPending,
  };
}
